import { useCallback, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import type { Editor, Extension, FocusPosition } from '@tiptap/react';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { BASE_EXTENSIONS } from './utils';
import { EditorState } from 'prosemirror-state';
import { editorHandleDrop } from './editorUtils';

export type FormatType =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'bold'
  | 'bulletList'
  | 'orderedList'
  | 'link'
  | 'image';

interface Props {
  defaultContent?: string;
  placeholderText?: string | null;
  characterLimit?: number;
  onUpdate?: (editor: Editor) => void;
  onInit?: (editor: Editor) => void;
  onError?: (error: string) => void;
  handleFileUpload?: (file: File) => Promise<string | null>;
  enableImages?: boolean;
}

export const useRichTextEditor = ({
  defaultContent = '<p></p>',
  placeholderText = 'Start by typing something here',
  characterLimit,
  onUpdate,
  onInit,
  onError,
  handleFileUpload,
}: Props = {}) => {
  const { current: extensions } = useRef(
    [
      History,
      ...BASE_EXTENSIONS,
      placeholderText &&
        typeof placeholderText === 'string' &&
        Placeholder.configure({
          includeChildren: true,
          showOnlyCurrent: false,
          placeholder: ({ pos, editor }) => {
            if (pos !== 0 || editor.getText().length > 0) return '';
            return placeholderText;
          },
        }),
      CharacterCount.configure({
        limit: characterLimit,
      }),
    ].filter(Boolean) as Extension[]
  );

  const editor = useEditor({
    onUpdate: ({ editor }) => {
      if (
        typeof characterLimit !== 'undefined' &&
        editor.storage.characterCount.characters() === characterLimit
      ) {
        onError?.(`Max ${characterLimit} character limit reached`);
      } else {
        onUpdate?.(editor as Editor);
      }
    },
    onCreate: ({ editor }) => {
      onInit?.(editor as Editor);
    },
    extensions,
    content: defaultContent,
    editorProps: {
      handleDrop: editorHandleDrop(handleFileUpload),
    },
  });

  const format = useCallback(
    (formatType: FormatType) => {
      switch (formatType) {
        case 'heading1':
          editor?.chain().focus().toggleHeading({ level: 3 }).run();
          break;
        case 'heading2':
          editor?.chain().focus().toggleHeading({ level: 4 }).run();
          break;
        case 'heading3':
          editor?.chain().focus().toggleHeading({ level: 5 }).run();
          break;
        case 'bold':
          editor?.chain().focus().toggleBold().run();
          break;
        case 'bulletList':
          editor?.chain().focus().toggleBulletList().run();
          break;
        case 'orderedList':
          editor?.chain().focus().toggleOrderedList().run();
          break;
      }
    },
    [editor]
  );

  const isActive = useCallback(
    (formatType: FormatType) => {
      switch (formatType) {
        case 'heading1':
          return editor?.isActive('heading', { level: 3 });
        case 'heading2':
          return editor?.isActive('heading', { level: 4 });
        case 'heading3':
          return editor?.isActive('heading', { level: 5 });
        case 'bold':
        case 'bulletList':
        case 'orderedList':
        case 'image':
        case 'link':
        default:
          return editor?.isActive(formatType);
      }
    },
    [editor]
  );

  const focus = useCallback(
    (focusPostion: FocusPosition) => editor?.commands.focus(focusPostion),
    [editor]
  );

  const getCharacterCount = useCallback(
    (): number => editor?.storage.characterCount.characters() ?? 0,
    [editor]
  );

  const setContent = useCallback(
    (content: string) => {
      if (editor) {
        editor.commands.setContent(content);
      }
    },
    [editor]
  );

  const resetEditorContent = useCallback(() => {
    if (editor) {
      editor.commands.setContent(defaultContent);
      const newEditorState = EditorState.create({
        doc: editor.state.doc,
        plugins: editor.state.plugins,
        schema: editor.state.schema,
      });

      if (!editor.view.isDestroyed) {
        editor.view.updateState(newEditorState);
      }
    }
  }, [editor, defaultContent]);

  return {
    editor,
    focus,
    getCharacterCount,
    format,
    isActive,
    setContent,
    resetEditorContent,
  };
};
